import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { UserPlus, Trash2, BadgeCheck, Clock, Mail } from "lucide-react";
import { tripsApi, travelersApi, type TripTraveler } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface Props {
  tripId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface RosterTraveler {
  id: string;
  display_name: string;
}

/**
 * Add & manage the people going on a trip. Adding a traveler with an email
 * shares the trip with them: an existing TripMuscle user sees it in their
 * account right away, a non-user is invited by email. The owner picks a role
 * (Viewer/Editor) per traveler. This is the real "add to trip" action that
 * replaces the old link-to-profile affordance.
 */
export default function TripTravelersDialog({ tripId, open, onOpenChange }: Props) {
  const queryClient = useQueryClient();
  const [existingId, setExistingId] = useState<string>("new");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<"viewer" | "editor">("viewer");
  const [formError, setFormError] = useState("");

  const travelersQuery = useQuery({
    queryKey: ["tripTravelers", tripId],
    queryFn: () => tripsApi.listTravelers(tripId),
    enabled: open,
  });

  // The owner's saved roster, to pick an existing traveler instead of retyping.
  const rosterQuery = useQuery({
    queryKey: ["travelers"],
    queryFn: () => travelersApi.list() as Promise<{ travelers: RosterTraveler[] }>,
    enabled: open,
  });

  const shares: TripTraveler[] = travelersQuery.data?.travelers ?? [];
  const roster: RosterTraveler[] = rosterQuery.data?.travelers ?? [];

  const resetForm = () => {
    setExistingId("new");
    setName("");
    setEmail("");
    setRole("viewer");
    setFormError("");
  };

  const addMutation = useMutation({
    mutationFn: () =>
      tripsApi.addTraveler(tripId, {
        travelerId: existingId !== "new" ? existingId : undefined,
        name: existingId === "new" ? name.trim() : undefined,
        email: email.trim() || undefined,
        role,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tripTravelers", tripId] });
      queryClient.invalidateQueries({ queryKey: ["tripPlan", tripId] });
      resetForm();
    },
    onError: (e: unknown) => {
      const msg =
        (e as { response?: { data?: { error?: string } } })?.response?.data?.error ??
        "Could not add the traveler.";
      setFormError(msg);
    },
  });

  const removeMutation = useMutation({
    mutationFn: (shareId: string) => tripsApi.removeTraveler(tripId, shareId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["tripTravelers", tripId] }),
  });

  const onAdd = () => {
    setFormError("");
    if (existingId === "new" && !name.trim()) {
      setFormError("Enter a name, or pick someone you've saved before.");
      return;
    }
    addMutation.mutate();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Travelers on this trip</DialogTitle>
          <DialogDescription>
            Add the people going with you. With an email, we&apos;ll share the trip so they can
            track fares too — and invite them if they&apos;re not on TripMuscle yet.
          </DialogDescription>
        </DialogHeader>

        {/* Current travelers */}
        {shares.length > 0 && (
          <ul className="space-y-1.5">
            {shares.map((s) => (
              <li
                key={s.share_id}
                className="flex items-center justify-between gap-2 rounded-lg border bg-card px-3 py-2 text-sm"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5 font-medium">
                    <span className="truncate">{s.display_name || s.email}</span>
                    <span className="rounded-full bg-muted px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">
                      {s.role}
                    </span>
                  </div>
                  <div className="mt-0.5 flex items-center gap-1 text-xs text-muted-foreground">
                    {s.is_user ? (
                      <>
                        <BadgeCheck className="h-3.5 w-3.5 text-emerald-500" aria-hidden="true" />
                        {s.status === "active" ? "Shared — on TripMuscle" : "TripMuscle user"}
                      </>
                    ) : (
                      <>
                        <Clock className="h-3.5 w-3.5" aria-hidden="true" />
                        Invited — {s.email}
                      </>
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => removeMutation.mutate(s.share_id)}
                  disabled={removeMutation.isPending}
                  className="rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
                  aria-label={`Remove ${s.display_name || s.email}`}
                >
                  <Trash2 className="h-4 w-4" aria-hidden="true" />
                </button>
              </li>
            ))}
          </ul>
        )}

        {/* Add form */}
        <div className="space-y-3 rounded-lg border border-dashed p-3">
          {roster.length > 0 && (
            <div className="space-y-1.5">
              <Label>Who&apos;s going?</Label>
              <Select value={existingId} onValueChange={setExistingId}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="new">+ Someone new</SelectItem>
                  {roster.map((t) => (
                    <SelectItem key={t.id} value={t.id}>
                      {t.display_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {existingId === "new" && (
            <div className="space-y-1.5">
              <Label htmlFor="tt-name">Name</Label>
              <Input
                id="tt-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Leithia Warren"
              />
            </div>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="tt-email">
              <span className="inline-flex items-center gap-1">
                <Mail className="h-3.5 w-3.5" aria-hidden="true" /> Email{" "}
                <span className="font-normal text-muted-foreground">(to share &amp; notify)</span>
              </span>
            </Label>
            <Input
              id="tt-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="name@email.com"
            />
          </div>

          <div className="space-y-1.5">
            <Label>Access</Label>
            <Select value={role} onValueChange={(v: "viewer" | "editor") => setRole(v)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="viewer">Viewer — can see the trip &amp; fares</SelectItem>
                <SelectItem value="editor">Editor — can also make changes</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {formError && <p className="text-sm text-destructive">{formError}</p>}

          <Button type="button" onClick={onAdd} disabled={addMutation.isPending} className="w-full">
            <UserPlus className="mr-1.5 h-4 w-4" aria-hidden="true" />
            {addMutation.isPending ? "Adding…" : "Add to trip"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
